"use client";

import React, { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { Filter } from 'lucide-react';
import ProjectCard from '@/components/ProjectCard';
import { projects } from '@/data/projects';
import clsx from 'clsx';

const ProjectsPage: React.FC = () => {
  const headerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [filteredProjects, setFilteredProjects] = useState(projects);

  const categories = ['All', ...Array.from(new Set(projects.map(p => p.category)))];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" }
      );
    }, headerRef);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (selectedCategory === 'All') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(p => p.category === selectedCategory));
    }
  }, [selectedCategory]);

  const handleCategoryChange = (category: string) => {
    if (category === selectedCategory) return;

    // Animate out current projects
    const currentCards = gridRef.current?.children;
    if (currentCards) {
      gsap.to(Array.from(currentCards), {
        opacity: 0,
        y: 20,
        duration: 0.3,
        stagger: 0.05,
        ease: "power2.in",
        onComplete: () => {
          setSelectedCategory(category);
          // Animation in will be handled by ProjectCard component
        }
      });
    } else {
      setSelectedCategory(category);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, category: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCategoryChange(category);
    }
  };

  return (
    <div className="bg-background text-foreground">
      {/* Header Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div ref={headerRef} className="max-w-4xl mx-auto text-center">
            <h1 className="text-6xl md:text-8xl font-bold mb-8 leading-tight">
              Selected
              <br />
              <span className="text-accent">Projects</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              A collection of award-winning digital experiences crafted with 
              attention to detail, innovative technology, and user-centered design.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="pb-12 border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2 text-gray-600">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">Filter by category</span>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                  onKeyDown={(e) => handleKeyDown(e, category)}
                  className={clsx(
                    "px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2",
                    {
                      "bg-accent text-background": selectedCategory === category,
                      "bg-muted text-foreground hover:bg-gray-200": selectedCategory !== category,
                    }
                  )}
                  tabIndex={0}
                  role="button"
                  aria-pressed={selectedCategory === category}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div
            ref={gridRef}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr"
          >
            {filteredProjects.map((project, index) => (
              <div
                key={`${project.id}-${selectedCategory}`}
                className={clsx({
                  "md:col-span-2 md:row-span-2": project.featured && filteredProjects.length > 3,
                })}
              >
                <ProjectCard 
                  project={project} 
                  index={index}
                />
              </div>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">
                No projects found in this category.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Start Your
              <br />
              <span className="text-accent">Next Project?</span>
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Let&apos;s collaborate to create something extraordinary that will 
              make your brand stand out and engage your audience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-accent text-background px-8 py-4 rounded-full hover:bg-gray-800 transition-all duration-300 font-medium"
              >
                Get In Touch
              </a>
              <a
                href="mailto:hello@portfolio.com"
                className="border border-accent text-accent px-8 py-4 rounded-full hover:bg-accent hover:text-background transition-all duration-300 font-medium"
              >
                Send Email
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;